import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatDate } from '@angular/common';

import { Utils } from '../../../shared/utils';
import { ConfirmService } from '../../shared/services/confirm.service';
import { NotificationService } from '../../shared/services/notification.service';
import { TierService } from '../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../shared/components/confirm-dialog.component';
import {
  FINANCING_LONG_TERM,
  FinancingDetailAction,
  FinancingDetailConstitution,
  FinancingDetailDeleter,
  FinancingDetailLoader,
  FinancingDetailPayment,
  FinancingDetailRow,
  FinancingDetailShortTermDebt,
  FinancingOperation,
  financingTermLabel,
  isFinancingAmortized,
  stripFinancingConcept,
} from './financing-detail.model';

interface FinancingMovementLine {
  uid: string;
  date: Date | string;
  concept: string;
  meta: string;
  amount: number;
}

interface FinancingMovementBlock {
  operation: FinancingOperation;
  title: string;
  icon: string;
  empty: string;
  count: number;
  total: number;
  note: string;
  canAdd: boolean;
  lines: FinancingMovementLine[];
}

interface FinancingDeleteConfig {
  title: string;
  message: string;
  loadingText: string;
  errorText: string;
  successText: string;
  amountLabel: string;
}

const MAX_ROWS = 5;

const DELETE_CONFIG: Record<FinancingOperation, FinancingDeleteConfig> = {
  payInstallments: {
    title: 'Eliminar pago de cuota',
    message: 'Se eliminará el pago, se devolverá el capital al importe pendiente y se borrará su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente.',
    loadingText: 'Eliminando pago…',
    errorText: 'Ha ocurrido un error al eliminar el pago',
    successText: 'Pago eliminado correctamente',
    amountLabel: 'Capital',
  },
  constitution: {
    title: 'Eliminar constitución',
    message: 'Se eliminará la constitución y su asiento contable. Si estaba conciliada, el movimiento bancario volverá a quedar pendiente.',
    loadingText: 'Eliminando constitución…',
    errorText: 'Ha ocurrido un error al eliminar la constitución',
    successText: 'Constitución eliminada correctamente',
    amountLabel: 'Importe',
  },
  shortTermDebt: {
    title: 'Eliminar deuda a corto plazo',
    message: 'Se eliminará la reclasificación a corto plazo y su asiento contable. Esta acción no se puede deshacer.',
    loadingText: 'Eliminando deuda a corto plazo…',
    errorText: 'Ha ocurrido un error al eliminar el elemento',
    successText: 'Deuda a corto plazo eliminada correctamente',
    amountLabel: 'Importe',
  },
};

@Component({
  selector: 'app-financing-detail-panel',
  templateUrl: './financing-detail-panel.component.html',
  styleUrls: ['./financing-detail-panel.component.scss'],
  standalone: false,
})
export class FinancingDetailPanelComponent implements OnChanges {
  @Input() row!: FinancingDetailRow;
  @Input() loader!: FinancingDetailLoader;
  @Input() deleter!: FinancingDetailDeleter;
  @Output() action = new EventEmitter<FinancingDetailAction>();

  readonly maxRows = MAX_ROWS;

  loading = true;
  error = false;

  constituciones: FinancingDetailConstitution[] = [];
  pagos: FinancingDetailPayment[] = [];
  deudas: FinancingDetailShortTermDebt[] = [];
  blocks: FinancingMovementBlock[] = [];

  private loadedSignature = '';

  constructor(
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get termLabel(): string {
    return financingTermLabel(this.row && this.row.duracion);
  }

  get isLongTerm(): boolean {
    return !!this.row && this.row.duracion === FINANCING_LONG_TERM;
  }

  get amortized(): boolean {
    return !!this.row && isFinancingAmortized(this.row);
  }

  get initialAmount(): number {
    return this.round(Number(this.row && this.row.importeInicial) || 0);
  }

  get pendingAmount(): number {
    return this.round(Number(this.row && this.row.importePendiente) || 0);
  }

  get amortizedAmount(): number {
    return this.round(Math.max(0, this.initialAmount - this.pendingAmount));
  }

  get percent(): number {
    if (this.initialAmount <= 0) return 0;
    return Math.min(100, Math.max(0, (this.amortizedAmount / this.initialAmount) * 100));
  }

  get capitalPaid(): number {
    return this.sum(this.pagos, p => p.importeCapital);
  }

  get interestPaid(): number {
    return this.sum(this.pagos, p => p.intereses);
  }

  get constituted(): number {
    return this.sum(this.constituciones, c => c.importe);
  }

  get reclassified(): number {
    return this.sum(this.deudas, d => d.importe);
  }

  ngOnChanges(): void {
    if (!this.row || !this.loader) return;
    const signature = [this.row.uid, this.row.importeInicial, this.row.importePendiente, this.row.__detailVersion || 0].join('|');
    if (signature === this.loadedSignature) return;
    this.loadedSignature = signature;
    this.load(this.row.uid);
  }

  retry(): void {
    this.loadedSignature = '';
    this.ngOnChanges();
  }

  add(block: FinancingMovementBlock): void {
    this.action.emit({ type: block.operation });
  }

  manage(block: FinancingMovementBlock): void {
    this.action.emit({ type: block.operation });
  }

  editLine(block: FinancingMovementBlock, line: FinancingMovementLine): void {
    this.action.emit({ type: block.operation, editUid: line.uid });
  }

  deleteLine(block: FinancingMovementBlock, line: FinancingMovementLine): void {
    if (!this.deleter || !this.tier.requireAdvancedTier()) return;
    const config = DELETE_CONFIG[block.operation];
    this.confirm.open({
      title: config.title,
      subtitle: this.row.descripcion || undefined,
      message: config.message,
      tone: 'danger',
      details: this.details(config, line),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: config.loadingText,
      errorText: config.errorText,
      action: () => this.deleter(block.operation, line.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok(config.successText);
      this.action.emit({ type: 'changed' });
    });
  }

  trackByOperation(_: number, block: FinancingMovementBlock): string {
    return block.operation;
  }

  trackByUid(_: number, item: { uid: string }): string {
    return item.uid;
  }

  private load(uid: string): void {
    this.loading = !this.blocks.length;
    this.error = false;
    this.loader(uid)
      .then(data => {
        if (!this.row || uid !== this.row.uid) return;
        this.constituciones = (data && data.constituciones) || [];
        this.pagos = (data && data.pagos) || [];
        this.deudas = (data && data.deudas) || [];
        this.blocks = this.buildBlocks();
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }

  private buildBlocks(): FinancingMovementBlock[] {
    const pagos = this.sortByDate(this.pagos);
    const constituciones = this.sortByDate(this.constituciones);
    const deudas = this.sortByDate(this.deudas);

    const blocks: FinancingMovementBlock[] = [
      {
        operation: 'payInstallments',
        title: 'Pagos de cuotas',
        icon: 'payment',
        empty: 'Todavía no hay pagos de cuotas registrados',
        count: pagos.length,
        total: this.capitalPaid,
        note: this.interestPaid ? 'Intereses ' + Utils.FormatMoney(this.interestPaid) + ' €' : '',
        canAdd: true,
        lines: pagos.slice(0, MAX_ROWS).map(item => ({
          uid: item.uid,
          date: item.fecha,
          concept: this.concept(item.concepto),
          meta: [
            Number(item.intereses) ? 'Intereses ' + Utils.FormatMoney(item.intereses) + ' €' : '',
            Number(item.gastosBancarios) ? 'Gastos ' + Utils.FormatMoney(item.gastosBancarios) + ' €' : '',
            item.bankAccountInfo,
          ].filter(Boolean).join(' · '),
          amount: Number(item.importeCapital) || 0,
        })),
      },
      {
        operation: 'constitution',
        title: 'Constituciones',
        icon: 'account_balance',
        empty: 'Sin constitución registrada',
        count: constituciones.length,
        total: this.constituted,
        note: '',
        canAdd: true,
        lines: constituciones.slice(0, MAX_ROWS).map(item => ({
          uid: item.uid,
          date: item.fecha,
          concept: this.concept(item.concepto),
          meta: [
            Number(item.comisionApertura) ? 'Comisión ' + Utils.FormatMoney(item.comisionApertura) + ' €' : '',
            Number(item.gastosBancarios) ? 'Gastos ' + Utils.FormatMoney(item.gastosBancarios) + ' €' : '',
            item.bankAccountInfo,
          ].filter(Boolean).join(' · '),
          amount: Number(item.importe) || 0,
        })),
      },
    ];

    if (this.isLongTerm || deudas.length) {
      blocks.push({
        operation: 'shortTermDebt',
        title: 'Deuda a corto plazo',
        icon: 'swap_vert',
        empty: 'No se ha reclasificado deuda a corto plazo',
        count: deudas.length,
        total: this.reclassified,
        note: '',
        canAdd: this.isLongTerm,
        lines: deudas.slice(0, MAX_ROWS).map(item => ({
          uid: item.uid,
          date: item.fecha,
          concept: this.concept(item.concepto),
          meta: item.activitiesName || '',
          amount: Number(item.importe) || 0,
        })),
      });
    }

    return blocks;
  }

  private concept(value: string): string {
    return stripFinancingConcept(value, this.row && this.row.descripcion);
  }

  private details(config: FinancingDeleteConfig, line: FinancingMovementLine): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (line.date) details.push({ label: 'Fecha', value: formatDate(line.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Concepto', value: line.concept || 'Sin concepto' });
    if (line.meta) details.push({ label: 'Detalle', value: line.meta });
    details.push({ label: config.amountLabel, value: Utils.FormatMoney(line.amount) + ' €' });
    return details;
  }

  private sum<T>(items: T[], fn: (item: T) => number): number {
    return this.round(items.reduce((acc, item) => acc + (Number(fn(item)) || 0), 0));
  }

  private sortByDate<T extends { fecha: Date | string }>(items: T[]): T[] {
    return [...items].sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  }

  private round(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}
